import { base } from './db'
import { libelleSecteur } from '../domaine/secteurs'
import { zonage } from './zonage'
import type { Observation, Patrouille, Signalement } from '../domaine/types'

/*
  Export CSV depuis l'appareil, sans réseau.

  Le serveur produit déjà l'export de la saison entière (toutes les
  patrouilles, tous les appareils) : c'est celui du bilan annuel. Celui-ci
  est différent et volontairement plus modeste — il ne contient que ce qui a
  été saisi sur CE téléphone, mais il fonctionne sur la plage, sans réseau,
  y compris avant toute synchronisation.

  Les colonnes sont IDENTIQUES à celles du serveur : les deux fichiers se
  concatènent sans retouche.
*/

/**
 * Échappe une valeur selon RFC 4180.
 *
 * Une note de terrain contient très facilement une virgule (« lampadaires,
 * face à la plage ») ou un guillemet. Sans échappement, le tableur décale
 * silencieusement toutes les colonnes suivantes — et personne ne s'en aperçoit
 * avant le bilan annuel.
 */
export function echapper(valeur: unknown): string {
  if (valeur === null || valeur === undefined) return ''

  const texte = String(valeur)
  // Le point-virgule est inclus : certains tableurs francophones l'utilisent
  // comme séparateur, et une note qui en contient casserait leur lecture.
  if (/[",;\r\n]/.test(texte)) {
    return `"${texte.replaceAll('"', '""')}"`
  }
  return texte
}

export function ligneCsv(valeurs: unknown[]): string {
  return valeurs.map(echapper).join(',')
}

export function construireCsv(entetes: string[], lignes: unknown[][]): string {
  // BOM UTF-8 : sans lui, Excel en français massacre les accents — « Émergence »
  // devient « Ãmergence ». C'est le premier retour qu'on aurait de l'association.
  return '﻿' + [ligneCsv(entetes), ...lignes.map(ligneCsv)].join('\r\n') + '\r\n'
}

export const ENTETES_OBSERVATIONS = [
  'patrouille_id',
  'secteur',
  'debut_patrouille',
  'fin_patrouille',
  'equipe',
  'observation_id',
  'type',
  'espece',
  'quantite',
  'horodatage',
]

export const ENTETES_SIGNALEMENTS = [
  'signalement_id',
  'type',
  'secteur',
  'quantite',
  'note',
  'a_une_photo',
  'horodatage',
]

export function lignesObservations(
  observations: Observation[],
  patrouilles: Map<string, Patrouille>,
  nomSecteur: (code: string) => string,
): unknown[][] {
  return [...observations]
    .sort((a, b) => a.ts.localeCompare(b.ts))
    .map((o) => {
      const p = patrouilles.get(o.patrouille_id)
      return [
        o.patrouille_id,
        p ? nomSecteur(p.secteur) : '',
        p?.debut ?? '',
        p?.fin ?? '',
        p?.equipe ?? '',
        o.id,
        o.type,
        o.espece ?? '',
        o.quantite,
        o.ts,
      ]
    })
}

export function lignesSignalements(
  signalements: Signalement[],
  nomSecteur: (code: string) => string,
): unknown[][] {
  return [...signalements]
    .sort((a, b) => a.ts.localeCompare(b.ts))
    .map((s) => [
      s.id,
      s.type,
      nomSecteur(s.secteur),
      s.quantite ?? '',
      s.note ?? '',
      s.photo_id ? 'oui' : 'non',
      s.ts,
    ])
}

export type TableExport = 'observations' | 'signalements'

/** Construit le CSV à partir des seules données locales. Aucun appel réseau. */
export async function csvLocal(table: TableExport): Promise<string> {
  const db = await base()
  const secteurs = await zonage()
  const nomSecteur = (code: string) => libelleSecteur(code, secteurs)

  if (table === 'signalements') {
    return construireCsv(
      ENTETES_SIGNALEMENTS,
      lignesSignalements(await db.getAll('signalements'), nomSecteur),
    )
  }

  const patrouilles = new Map(
    (await db.getAll('patrouilles')).map((p) => [p.id, p] as const),
  )
  return construireCsv(
    ENTETES_OBSERVATIONS,
    lignesObservations(await db.getAll('observations'), patrouilles, nomSecteur),
  )
}

/** Nom de fichier daté, pour que deux exports ne s'écrasent pas. */
export function nomFichier(table: TableExport, maintenant = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  const j = `${maintenant.getFullYear()}-${p(maintenant.getMonth() + 1)}-${p(maintenant.getDate())}`
  return `kwata-${table}-appareil-${j}.csv`
}

/**
 * Déclenche le téléchargement.
 *
 * Sur Android/Chrome, le fichier atterrit dans « Téléchargements ».
 * Sur iOS, en application installée sur l'écran d'accueil, le comportement
 * dépend de la version : le fichier s'ouvre parfois dans une vue de partage
 * au lieu d'être enregistré. Voir la note du README.
 */
export function telecharger(nom: string, contenu: string): void {
  const blob = new Blob([contenu], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const lien = document.createElement('a')
  lien.href = url
  lien.download = nom
  document.body.append(lien)
  lien.click()
  lien.remove()

  // Laisser le temps au navigateur d'entamer le téléchargement.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
